import { createAsyncThunk } from "@src/store";
import { notifyBuilderEvent } from "@src/store/slices/experienceBuilder/common/thunks/notifyBuilderEvent";

import { cancelContinuation } from "../actions";
import { experienceBuilderAiSlice } from "../slice";
import { getVivChatWorkflowHandle } from "../vivChatWorkflowHandle";
import {
  prepareChatMutation,
  PrepareChatMutationKind,
} from "./prepareChatMutation";

const { beginTurn, endTurn } = experienceBuilderAiSlice.actions;

// Monotonic per-turn token so a turn's `endTurn` can tell "am I still the
// active turn?" — a resume that superseded an ending turn keeps the busy lock.
let turnTokenSeq = 0;

/**
 * Fires one Viv chat turn end-to-end — assemble the request, run the stream,
 * own the busy flag. Lives in Redux so both the component (user-typed turns)
 * and the side-effect listener (continuations) drive turns the same way:
 * `dispatch(fireVivTurn(kind))`.
 *
 * Holds the busy lock for the full turn (assemble request + stream), and
 * no-ops if the chat closed before it could run (the handle is gone).
 */
export const fireVivTurn = createAsyncThunk<void, PrepareChatMutationKind>(
  "experienceBuilderAi/fireVivTurn",
  async (kind, { dispatch }) => {
    const handle = getVivChatWorkflowHandle();
    if (!handle) {
      // Chat closed before this turn could fire (e.g. a continuation queued
      // for a now-closed dialog) — release the busy lock and stop.
      return;
    }

    // `beginTurn` claims a fresh token AND clears `continuationInFlight`,
    // handing the busy lock off from any continuation sequence to this turn
    // without a gap.
    const token = ++turnTokenSeq;
    dispatch(beginTurn(token));
    try {
      const payload = await dispatch(prepareChatMutation(kind)).unwrap();
      await handle.mutate(payload);
    } catch {
      // `PageHtmlUnavailableError` surfaces the `pageHtmlUnavailable` overlay
      // from inside `prepareChatMutation`; the input unlocks as the lock clears
      // below. No further handling needed.
    } finally {
      // Releases only if we're still the active turn — a resume that superseded
      // this turn (client-tool handoff race) keeps the lock.
      dispatch(endTurn(token));
    }
  }
);

/**
 * Stops the chat: aborts any queued continuation, then tears down the stream
 * via the registered handle (cancel backend + abort fetch + reset chat state).
 *
 * A `user_stop` (the Stop button) mirrors an "Interrupted" card into the Slack
 * thread. `close_builder` deliberately does not — that path is already
 * represented by the save/discard card `closeBuilder` emits.
 */
export const stopVivChat = createAsyncThunk<void, string | undefined>(
  "experienceBuilderAi/stopVivChat",
  async (source, { dispatch }) => {
    dispatch(cancelContinuation());
    getVivChatWorkflowHandle()?.stop(source);
    if (source === "user_stop")
      void dispatch(notifyBuilderEvent("interrupted"));
  }
);
